import { IFormElementBaseConfig } from '../../_shared/models/form/form-element-base.model';
import { IFormSelect } from '../../_shared/models/form/form-select.model';
import { FormSubmissionData } from '../../_shared/models/form/form-submission-data.model';
import { ConversionApiNormalizerService } from './form-conversion-api-normalizer.service';
import { IConversionApiField } from './form-conversion.model';

export class ConversionApiHelperService {
    private _identifiersMap: Map<string, string> = new Map();
    private _namesMap: Map<string, string> = new Map();
    private _selectValuesMap: Map<string, string> = new Map();
    private _submissionData?: FormSubmissionData;

    set submissionData(value: FormSubmissionData) {
        this._submissionData = value;
    }

    constructor(fields: IFormElementBaseConfig[]) {
        if (fields) {
            fields.map((field) => {
                if (!field.identifier) return;

                this._populateMaps(field);

                field.dependentFormFieldComponents?.map((x) => {
                    this._populateMaps(x.dependentFormField);
                });
            });
        }
    }

    async getNormalizedObj(
        data: IConversionApiField[],
        countryField?: IConversionApiField['name']
    ): Promise<{ [key: string]: string | undefined }> {
        return await this._addValuesToFields(data).then((x) => {
            const normalizer = new ConversionApiNormalizerService(x, countryField);
            return normalizer.getNormalizedFields();
        });
    }

    public getNameByIdentifier(identifier: string): string | undefined {
        return this._identifiersMap.get(identifier.toLowerCase()) ?? undefined;
    }

    public getIdentifierByName(name: string): string | undefined {
        return this._namesMap.get(name.toLowerCase()) ?? undefined;
    }

    public getValue(name: string): string | undefined {
        if (!this._submissionData) throw new Error('Submission data is undefined.');
        return this._submissionData.fields[name] ?? undefined;
    }

    public getFieldValue(identifier: string): string | undefined {
        const name = this.getNameByIdentifier(identifier);
        return name ? this.getValue(name) : undefined;
    }

    public getSelectValueByIdentifier(identifier: string): string | undefined {
        const key = this.getFieldValue(identifier);
        return key ? this._selectValuesMap.get(key) : undefined;
    }

    private _populateMaps = (field: IFormElementBaseConfig) => {
        if (field.identifier && field.name) {
            const identifier = field.identifier.toLowerCase();
            const name = field.name.toLowerCase();

            this._identifiersMap.set(identifier, name);
            this._namesMap.set(name, identifier);
        }

        if (field.componentName === 'form-select') {
            const formSelectField = field as IFormSelect;
            const options = formSelectField.options;

            if (options) {
                options.map((x) => {
                    this._selectValuesMap.set(x.value, x.text);
                });
            }
        }
    };

    private async _addValuesToFields(
        fields: IConversionApiField[]
    ): Promise<IConversionApiField[]> {
        return fields.map((field) => {
            const identifier = field.options.identifier;

            const _id = Array.isArray(identifier)
                ? identifier.find((x) => {
                      const name = this.getNameByIdentifier(x);
                      return name ? this.getValue(name) : undefined;
                  })
                : identifier;

            const _val = _id
                ? field.options.element === 'select'
                    ? this.getSelectValueByIdentifier(_id)
                    : this.getFieldValue(_id)
                : undefined;

            field.value = _val;

            return field;
        });
    }
}
